import * as PropTypes from 'prop-types';

import {User, USER_SHAPE} from 'constants/UserConstants';

export enum WritableAssayValidationStatus {
  VALID = 'VALID',
  INVALID = 'INVALID',
}
export enum ReadableAssayValidationStatus {
  VALID = 'VALID',
  PARTIALLY_VALID = 'PARTIALLY_VALID',
  INVALID = 'INVALID',
}

export const STATUS_TO_NAME = {
  [WritableAssayValidationStatus.VALID]: 'Valid',
  [WritableAssayValidationStatus.INVALID]: 'Invalid',
};

export type AssayValidationComment = string | null;

export interface CurrentAssayValidation {
  status: ReadableAssayValidationStatus;
  comment: AssayValidationComment;
}

export interface AssayValidationChange {
  status: WritableAssayValidationStatus;
  comment: AssayValidationComment;
  creator: User;
  createdAt: string;
}
const ASSAY_VALIDATION_CHANGE_SHAPE = PropTypes.shape({
  status: PropTypes.oneOf(Object.values(WritableAssayValidationStatus)).isRequired,
  comment: PropTypes.string,
  creator: USER_SHAPE.isRequired,
  createdAt: PropTypes.string.isRequired,
});

export type AssayValidationHistory = Array<AssayValidationChange>;
interface AssayValidationWithHistory extends CurrentAssayValidation {
  history: AssayValidationHistory;
}
const VALIDATION_WITH_HISTORY_SHAPE = PropTypes.shape({
  status: PropTypes.oneOf(Object.values(ReadableAssayValidationStatus)).isRequired,
  comment: PropTypes.string,
  history: PropTypes.arrayOf(ASSAY_VALIDATION_CHANGE_SHAPE).isRequired,
});

export type AssayRunValidation = AssayValidationWithHistory;
export type AssayResultFieldValidation = AssayValidationWithHistory;

export interface AssayResultValidation extends CurrentAssayValidation {
  fieldValidation: {[schemaFieldApiId: string]: AssayResultFieldValidation};
}
export const ASSAY_RESULT_VALIDATION_SHAPE = PropTypes.shape({
  status: PropTypes.oneOf(Object.values(ReadableAssayValidationStatus)).isRequired,
  comment: PropTypes.string,
  fieldValidation: PropTypes.objectOf(VALIDATION_WITH_HISTORY_SHAPE).isRequired,
});

export interface AssayValidationByResultId {
  [resultId: string]: AssayResultValidation;
}

export interface FieldHasValueDictByResultId {
  [resultId: string]: {[schemaFieldApiId: string]: boolean};
}

export interface AssayValidationDisplayInfo {
  status: ReadableAssayValidationStatus;
  comment: AssayValidationComment;
  history?: AssayValidationHistory;
}

export enum ValidatableInputType {
  RUN = 'RUN',
  RESULT = 'RESULT',
  RESULT_FIELD = 'RESULT_FIELD',
}

export interface RunValidatableInput {
  type: ValidatableInputType.RUN;
  assayRunId: string;
  assayResultId: null;
  schemaFieldId: null;
}
export interface ResultValidatableInput {
  type: ValidatableInputType.RESULT;
  assayRunId: null;
  assayResultId: string;
  schemaFieldId: null;
}
export interface ResultFieldValidatableInput {
  type: ValidatableInputType.RESULT_FIELD;
  assayRunId: null;
  assayResultId: string;
  schemaFieldId: string;
}
export type ValidatableInput = RunValidatableInput | ResultValidatableInput | ResultFieldValidatableInput;

export const VALIDATABLE_INPUT_SHAPE = PropTypes.shape({
  type: PropTypes.oneOf(Object.values(ValidatableInputType)).isRequired,
  assayRunId: PropTypes.string,
  assayResultId: PropTypes.string,
  schemaFieldId: PropTypes.string,
});
